'use client';
import {
  HeaderEquipmentSearchIcon,
  HeaderSearchBarNoResults
} from 'components/pcc-components';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { getSeoUrl } from 'utils/links';
import { SHOP_ALL_CATEGORIES } from 'constants/links';
import { navigateTo } from '@cat-ecom/common/utils/browserUtils';
import useSearchSuggestions from '../../hooks/use-search-suggestions/use-search-suggestions';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { PAGE_BREAKPOINTS } from '@cat-ecom/pcc-components/src/constants/commonConstants';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {
  BlocksChangeEvent,
  BlocksSearchEvent
} from '@blocks-web-components/events';
import { CatSearchForm } from '@blocks-react/components/SearchForm';
import { CatIconSearch } from '@blocks-react/components/Icons/Search';
import { useSearchState } from './useSearchState';
import styles from './search-bar.module.scss';
import { SuggestionList } from './suggestion-list';
import { RedesignedSuggestionList } from './redesigned-suggestion-list';
import cx from 'classnames';
import { mapSuggestedProducts } from '../../utils/map-suggested-products';

type SearchBarProps = {
  isMobile?: boolean;
  isFeatureFlag?: boolean;
  size?: 'lg' | 'md' | 'sm';
  children?: React.ReactNode;
  isHeadlessPDPEnabled?: boolean;
  model?: string;
  serialNumber?: string;
  isHeadlessCategoryEnabled: boolean;
  isFederatedSearchEnabled: boolean;
  isSearchBarInHeader: boolean;
};

export const SearchBar = ({
  isMobile,
  isFeatureFlag = false,
  size = 'md',
  children,
  isHeadlessPDPEnabled,
  model,
  serialNumber,
  isHeadlessCategoryEnabled,
  isFederatedSearchEnabled,
  isSearchBarInHeader
}: SearchBarProps) => {
  const t = useTranslations();
  const { fireEvent } = useFormSubmitEvent();
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // lang should always be a two-digit string without numbers or special characters (like 'es');
  // esite should always be a string without numbers or special characters, except for the dash (like demo-dealer).
  const params = useParams();
  const seoUrl = getSeoUrl(params);
  const {
    onSearch,
    getSuggestions,
    suggestions: {
      suggestedKeyWords,
      suggestedProducts,
      suggestedCategories,
      suggestedDiagrams
    },
    hideResults,
    searchValue
  } = useSearchSuggestions({
    isHeadlessPDPEnabled,
    isFederatedSearchEnabled,
    model,
    serialNumber
  });
  const placeholder = isFeatureFlag
    ? t('SEARCH_FOR_PART_NUMBER_OR_NAME')
    : t('TOP_NAV_SEARCH');
  const noSuggestedResults = t('SEARCH_NO_SUGGESTED_RESULTS');
  const noSuggestedResultsTryAgain = t('NO_SUGGESTED_RESULTS_TRY_AGAIN');
  const [isActive, setIsActive] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const { partSearchedValue, updatePartSearch } = useSearchState(searchValue);
  const searchRef = useRef(null);

  const isEmptyResults = isMobile ? false : hideResults;

  const setSearchInputValue = useCallback(
    (value: string) => {
      updatePartSearch(value);
      return value?.toLowerCase().replace(/:/g, '');
    },
    [updatePartSearch]
  );

  const handleScroll = useCallback(() => {
    if (!isMobile) {
      const searchValue = setSearchInputValue(partSearchedValue);
      getSuggestions(searchValue);
      setIsDropdownVisible(false);
      if (searchRef.current) {
        searchRef.current.blur();
      }
    }
  }, [
    isMobile,
    partSearchedValue,
    getSuggestions,
    setSearchInputValue,
    setIsDropdownVisible
  ]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const isSearchActive = isMobile
    ? isActive
    : !hideResults && isDropdownVisible;

  const noSuggestedResultsMessage =
    isFeatureFlag || isMobile ? noSuggestedResultsTryAgain : noSuggestedResults;
  const dropDownPanelMessage =
    hideResults && isMobile ? placeholder : noSuggestedResultsMessage;
  const hasNoResults = isEmpty([
    ...suggestedKeyWords,
    ...suggestedProducts,
    ...suggestedCategories,
    ...suggestedDiagrams
  ]);
  const hasKeywordsOrCategories = !isEmpty([
    ...suggestedKeyWords,
    ...suggestedCategories
  ]);

  const handleEmptySearch = () => {
    const path = [seoUrl, SHOP_ALL_CATEGORIES].join('');
    navigateTo(path);
  };
  const handleOnSearch = (value: string) => {
    const trimmedValue = value?.trim();
    if (isFeatureFlag && !trimmedValue) {
      handleEmptySearch();
      return;
    }
    fireEvent({ formName: 'search', formContent: value });
    onSearch(value);
  };

  const onChangeHandler = (event: BlocksChangeEvent) => {
    const searchInputValue = event.detail.value ?? '';
    const searchValue = setSearchInputValue(searchInputValue);
    getSuggestions(searchValue);
    setIsDropdownVisible(searchInputValue?.trim().length > 0);
  };
  const onSearchHandler = (event: BlocksSearchEvent) => {
    const searchInputValue = event.detail.value ?? '';
    const searchValue = setSearchInputValue(searchInputValue);
    handleOnSearch(searchValue);
  };

  const products = mapSuggestedProducts(
    suggestedProducts,
    seoUrl,
    isHeadlessPDPEnabled
  );

  const getDropDownContent = () => {
    if (hasNoResults)
      return (
        <HeaderSearchBarNoResults
          message={dropDownPanelMessage}
          isMobile={isMobile}
          isFederatedSearchEnabled={isFederatedSearchEnabled}
        />
      );

    return isFederatedSearchEnabled ? (
      <RedesignedSuggestionList
        searchValue={searchValue}
        suggestedKeyWords={suggestedKeyWords}
        suggestedCategories={suggestedCategories}
        suggestedProducts={products}
        suggestedDiagrams={suggestedDiagrams}
        isHeadlessCategoryEnabled={isHeadlessCategoryEnabled}
        model={model}
        serialNumber={serialNumber}
      />
    ) : (
      <SuggestionList
        searchValue={searchValue}
        suggestedKeyWords={suggestedKeyWords}
        suggestedCategories={suggestedCategories}
        suggestedProducts={products}
        isHeadlessCategoryEnabled={isHeadlessCategoryEnabled}
      />
    );
  };

  if (isMobile) {
    return (
      <CatSearchForm
        status={undefined}
        buttonText="search"
        hideText
        size={'md'}
        className={styles['header-search-bar']}
        placeholder={placeholder}
        data-testid="cat-header-search-bar"
        onBlChange={onChangeHandler}
        onBlSearch={onSearchHandler}
        isEmpty={isEmptyResults}
        isActive={isSearchActive}
        mobileBreakPoint={PAGE_BREAKPOINTS.BLOCKS_LG}
        onBlMobileOverlayOpen={() => setIsActive(true)}
        onBlMobileOverlayClose={() => setIsActive(false)}
        position="bottom-start"
        value={partSearchedValue}
        hideSearchIcon={false}
        fieldId="cat-header-search-bar"
      >
        <Fragment key="search-input">
          {children}
          <CatIconSearch slot="before" />
          {getDropDownContent()}
        </Fragment>
      </CatSearchForm>
    );
  }

  return (
    <CatSearchForm
      status={undefined}
      size={isFeatureFlag ? size : 'md'}
      className={cx(styles['header-search-bar'], {
        [styles['header-search-bar--increment-drop-down-width']]:
          !hasNoResults && isFederatedSearchEnabled && hasKeywordsOrCategories,
        [styles['header-search-bar--no-max-height']]: isFederatedSearchEnabled
      })}
      placeholder={placeholder}
      data-testid="cat-header-search-bar"
      onBlChange={onChangeHandler}
      onBlSearch={onSearchHandler}
      isEmpty={isEmptyResults}
      isActive={isSearchActive}
      mobileBreakPoint={PAGE_BREAKPOINTS.BLOCKS_LG}
      onBlMobileOverlayOpen={() => setIsActive(true)}
      onBlMobileOverlayClose={() => setIsActive(false)}
      onBlSearchClose={() => setIsDropdownVisible(false)}
      position="bottom-start"
      value={partSearchedValue}
      hideSearchIcon
      hideText={isFeatureFlag}
      ref={searchRef}
      onFocus={() => setIsDropdownVisible(true)}
      fieldId="cat-header-search-bar"
    >
      <Fragment key="search-input">{getDropDownContent()}</Fragment>
      {children}
      {isFeatureFlag && !isFederatedSearchEnabled ? (
        <CatIconSearch
          slot="before"
          onClick={() => handleOnSearch(partSearchedValue)}
        />
      ) : (
        <HeaderEquipmentSearchIcon
          onClick={() => handleOnSearch(partSearchedValue)}
          isSearchBarInHeader={isSearchBarInHeader}
          size={isFederatedSearchEnabled ? size : 'md'}
        />
      )}
    </CatSearchForm>
  );
};
